import { useEffect, useRef } from "react";
import xClose from "../../../Assets/icon/journey/x-close.svg";
import { MISSION_ENTRY_SOURCE } from "../../../Services/journey";
import { MissionOverview } from "./MissionParts";
import useMission from "./useMission";
import { MISSION_SCREEN_COPY as C } from "./copy";
import "./style.css";

/**
 * "Your Mission" (Figma 11268:28241 / sheet 11268:28414) — opened from the
 * Journey Mission card. A bottom sheet on a phone, a centred dialog on a wider
 * screen. Reads `GET /journey/mission` when it opens, for the four elements
 * and the evolution actions (Evolve My Mission, Mission History).
 *
 * Esc, the X and a tap on the backdrop close it; page scroll is locked while
 * it is open and focus moves to the close button.
 */
function MissionSheet({ open, onClose }) {
  const closeRef = useRef(null);
  const { details, loading, error, reload, actions } = useMission({
    enabled: open,
    entrySource: MISSION_ENTRY_SOURCE.JOURNEY_HOME,
  });

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;
  const mission = details?.mission;

  return (
    <div className="journey-mission-sheet" role="presentation" onClick={onClose}>
      <div
        className="journey-mission-sheet__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="journey-mission-sheet-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="journey-mission-sheet__bar">
          <button
            ref={closeRef}
            type="button"
            className="journey-mission-sheet__close"
            aria-label={C.close}
            onClick={onClose}
          >
            <img src={xClose} alt="" width="20" height="20" />
          </button>
        </div>
        <div className="journey-mission-sheet__head">
          <h2 id="journey-mission-sheet-title" className="journey-mission-sheet__title">
            {C.title}
          </h2>
          <p className="journey-mission-sheet__subtitle">{C.subtitle}</p>
        </div>

        {loading && !details && <p className="journey-mission-sheet__status">{C.loading}</p>}
        {!loading && error && !details && (
          <div className="journey-mission-sheet__status" role="alert">
            <p>{C.error}</p>
            <button type="button" className="journey-text-btn" onClick={reload}>
              {C.retry}
            </button>
          </div>
        )}
        {mission?.statement && (
          <MissionOverview mission={mission} evolution={details.evolution} {...actions} />
        )}
      </div>
    </div>
  );
}

export default MissionSheet;
